import { useTranslation } from "react-i18next";

import { prependServiceBaseUrl } from "../../../../commons/libs/client-utils";
import { ModalCloseOptions } from "../../../../commons/types/modal";
import { PrintType } from "../../../../commons/types/print-mail";
import * as icons from "../../../../resources/icons";
import Icon from "../../components/Icon/Icon";
import IconButton from "../../components/IconButton/IconButton";
import IconLabel from "../../components/IconLabel/IconLabel";
import PdfViewer from "../../components/PdfViewer/PdfViewer";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import usePrintOrMail from "../../libs/hooks/use-print-or-mail";
import useQuery from "../../libs/hooks/use-query";
import { getPdfMetadataFromUrl } from "../../libs/services/pdf-service/pdf";
import { useCommonAppDispatcher } from "../../store/hooks";

interface Props {
  url: string;
  isOffline: boolean;
  isOfflineAndMailToPrintIsEnabled: boolean;
  hidePrint?: boolean;
  hideSend?: boolean;
  onClose: () => void;
}

const PdfViewerParial = ({
  url,
  isOffline,
  isOfflineAndMailToPrintIsEnabled,
  hidePrint = false,
  hideSend = false,
  onClose
}: Props) => {
  const [dispatch, actions] = useCommonAppDispatcher();
  const { t } = useTranslation(["commons"]);
  const breakpoints = useMediaQuery();

  const { print, mail, isPrintingActive, isMailActive } = usePrintOrMail();

  const pdf = useQuery({
    queryKey: ["pdf-metadata", url],
    queryFn: () => getPdfMetadataFromUrl(url),
    onError: () => {
      dispatch(
        actions.toasts.showToast({
          title: t("commons:pdfViewerPartialApp.errorWhileDisplayingPdfToast"),
          timeout: 5 * 1000,
          icon: icons.IconSmallInputError,
          kind: "negative"
        })
      );
      onClose();
    }
  });

  const pages =
    (!pdf.error && pdf.data && pdf.data.pages.length > 0 && pdf.data.pages.map(page => prependServiceBaseUrl(page))) ||
    [];

  return (
    <PdfViewer
      pages={pages}
      isLoading={pdf.isLoading}
      actionButtons={
        hidePrint && hideSend ? undefined : (
          <>
            {!hidePrint && isPrintingActive && (
              <Tooltip
                shouldCloseAutomatically
                content={
                  <IconLabel kind="negative" icon={<Icon source={icons.IconSmallAttention} />}>
                    {t("commons:pdfViewerPartialApp.offlineAndNoPrinterConnectedLabel")}
                  </IconLabel>
                }
              >
                {({ open }) => (
                  <IconButton
                    label={breakpoints.l ? t("commons:pdfViewerPartialApp.printLabel") : undefined}
                    icon={<Icon title="Drucken" source={icons.IconSmallPrinter} />}
                    disabled={pdf.isLoading || !!pdf.error}
                    onClick={() => {
                      if (isOfflineAndMailToPrintIsEnabled) {
                        open();
                      } else if (pdf.data) {
                        print(
                          {
                            type: PrintType.Default,
                            items: [
                              {
                                url: pdf.data.url,
                                date: new Date(),
                                title: t("commons:pdfViewerPartialApp.pdfPreview")
                              }
                            ]
                          },
                          (options?: ModalCloseOptions) => {
                            if (options?.preventReopenPreviousModal) {
                              dispatch(
                                actions.modal.showModal("pdfViewer", {
                                  url,
                                  isOffline,
                                  isOfflineAndMailToPrintIsEnabled,
                                  hidePrint,
                                  hideSend
                                })
                              );
                            }
                          }
                        );
                      }
                    }}
                  />
                )}
              </Tooltip>
            )}

            {!hideSend && isMailActive && (
              <Tooltip
                shouldCloseAutomatically
                content={
                  <IconLabel kind="negative" icon={<Icon source={icons.IconSmallAttention} />}>
                    {t("commons:pdfViewerPartialApp.deviceOfflineLabel")}
                  </IconLabel>
                }
              >
                {({ open }) => (
                  <IconButton
                    label={breakpoints.l ? t("commons:pdfViewerPartialApp.sendButton") : undefined}
                    icon={
                      <Icon title={t("commons:pdfViewerPartialApp.sendButton")} source={icons.IconSmallSendArrow} />
                    }
                    disabled={pdf.isLoading || !!pdf.error}
                    onClick={() => {
                      if (isOffline) {
                        open();
                      } else if (pdf.data) {
                        mail(
                          {
                            type: PrintType.Default,
                            items: [
                              {
                                url: pdf.data.url,
                                date: new Date(),
                                title: t("commons:pdfViewerPartialApp.pdfPreview")
                              }
                            ]
                          },
                          (options?: ModalCloseOptions) => {
                            if (options?.preventReopenPreviousModal) {
                              dispatch(
                                actions.modal.showModal("pdfViewer", {
                                  url,
                                  isOffline,
                                  isOfflineAndMailToPrintIsEnabled,
                                  hidePrint,
                                  hideSend
                                })
                              );
                            }
                          }
                        );
                      }
                    }}
                  />
                )}
              </Tooltip>
            )}
          </>
        )
      }
    />
  );
};

export default PdfViewerParial;
